/**
 * Binary game-state codec for the hot path (sent every tick).
 *
 * WebSocket delivery is reliable and ordered, so after one KEYFRAME the
 * server only sends per-tick DELTAs: one head cell + tail flag per snake.
 * A typical 8-player tick is ~60 bytes instead of several KB of JSON.
 */
import { effectsOf, type GameEngine } from '../engine/engine.js';
import { BONUS_KINDS, EFFECT_FIRE, type BonusKind, type DeathCause, type Dir, type FoodKind, type TickEvents } from '../engine/types.js';

const CAUSES: DeathCause[] = ['wall', 'self', 'snake', 'head_on', 'disconnect', 'zone', 'fire'];
const NO_KILLER = 0xff;

export const BIN_KEYFRAME = 1;
export const BIN_DELTA = 2;
export const BIN_INPUT = 3;
export const BIN_PING = 4;
export const BIN_PONG = 5;
/** A turn for a given tick (see encodeInputAt). */
export const BIN_INPUT_AT = 6;

const FLAG_ALIVE = 1;
/**
 * Burning (fire power-up). The effects byte is full, so this rides in a spare bit of the player
 * flags (bit 4 in both keyframes and deltas); decoders fold it back into `effects` as EFFECT_FIRE.
 * Older clients ignore the bit.
 */
const FLAG_FIRE = 16;
const fireFlag = (effects: number) => (effects & EFFECT_FIRE ? FLAG_FIRE : 0);
const withFire = (flags: number, effects: number) => (flags & FLAG_FIRE ? effects | EFFECT_FIRE : effects);

export interface WirePlayer {
  slot: number;
  alive: boolean;
  team: number;
  dir: Dir;
  score: number;
  kills: number;
  /** Active power-up effects (EFFECT_* bitmask). */
  effects: number;
  /** Lives left, capped at 3 (also 3 for unlimited); only meaningful for players who have lives. */
  lives: number;
  body: number[];
}
export interface WireFood {
  cell: number;
  kind: FoodKind;
}
export interface KeyframeMsg {
  type: 'keyframe';
  tick: number;
  phase: number; // 0 countdown, 1 running, 2 finished
  width: number;
  height: number;
  players: WirePlayer[];
  food: WireFood[];
  obstacles: number[];
}
export interface DeltaMsg {
  type: 'delta';
  tick: number;
  phase: number;
  players: Array<{
    slot: number;
    alive: boolean;
    dir: Dir;
    score: number;
    kills: number;
    effects: number;
    lives: number;
    head: number | null;
    tailPopped: boolean;
  }>;
  foodAdded: WireFood[];
  foodRemoved: number[];
  obstaclesAdded: number[];
  respawned: Array<{ slot: number; dir: Dir; body: number[] }>;
  /** Speed boost: a second head move this tick, applied after the regular ones. */
  extraMoves: Array<{ slot: number; head: number; tailPopped: boolean }>;
  /** Who died this tick and who got the kill (kill feed). */
  deaths: Array<{ slot: number; killedBy: number | null; cause: DeathCause }>;
  /** Boost cost: drop the last tail segment of these snakes (after all moves). */
  trimmed: number[];
  /** Electric shock: cut these snakes' bodies to `length` (after everything else). */
  shocked: Array<{ slot: number; length: number }>;
  /** Bonus points (revenge, bounty, close call), in wire units like scores. Already in the scores above. */
  bonuses: Array<{ slot: number; kind: BonusKind; points: number }>;
}

const PHASES = { countdown: 0, running: 1, finished: 2 } as const;

class Writer {
  private buf: Uint8Array;
  private view: DataView;
  pos = 0;
  constructor(size = 256) {
    this.buf = new Uint8Array(size);
    this.view = new DataView(this.buf.buffer);
  }
  private ensure(n: number) {
    if (this.pos + n <= this.buf.length) return;
    const next = new Uint8Array(Math.max(this.buf.length * 2, this.pos + n));
    next.set(this.buf);
    this.buf = next;
    this.view = new DataView(next.buffer);
  }
  u8(v: number) {
    this.ensure(1);
    this.view.setUint8(this.pos, v);
    this.pos += 1;
  }
  u16(v: number) {
    this.ensure(2);
    this.view.setUint16(this.pos, v);
    this.pos += 2;
  }
  u32(v: number) {
    this.ensure(4);
    this.view.setUint32(this.pos, v);
    this.pos += 4;
  }
  done(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }
}

class Reader {
  private view: DataView;
  pos = 0;
  constructor(data: Uint8Array) {
    this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  }
  get remaining() {
    return this.view.byteLength - this.pos;
  }
  /** Optional trailing u16 list (older encoders may omit it). */
  u16list(): number[] {
    if (this.remaining < 2) return [];
    const n = this.u16();
    const out: number[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = this.u16();
    return out;
  }
  u8() {
    return this.view.getUint8(this.pos++);
  }
  u16() {
    const v = this.view.getUint16(this.pos);
    this.pos += 2;
    return v;
  }
  u32() {
    const v = this.view.getUint32(this.pos);
    this.pos += 4;
    return v;
  }
}

const NO_CELL = 0xffff;
const clamp16 = (n: number) => Math.max(0, Math.min(0xffff, n));
/** Scores travel divided by the point scale (points are always whole multiples of it). */
const wireScore = (engine: GameEngine, score: number) => clamp16(Math.round(score / (engine.config.rules?.pointScale ?? 1)));
const wireLives = (left: number | null) => (left === null ? 3 : Math.min(3, left));

export function encodeKeyframe(engine: GameEngine): Uint8Array {
  const w = new Writer(512);
  w.u8(BIN_KEYFRAME);
  w.u32(engine.tick);
  w.u8(PHASES[engine.phase]);
  w.u16(engine.config.width);
  w.u16(engine.config.height);
  w.u8(engine.players.length);
  for (const p of engine.players) {
    const fx = effectsOf(p, engine.tick);
    w.u8(p.slot);
    w.u8((p.alive ? FLAG_ALIVE : 0) | ((p.team & 0x7) << 1) | fireFlag(fx));
    w.u8(fx & 0xff);
    w.u8(p.dir);
    w.u16(wireScore(engine, p.score));
    w.u16(clamp16(p.kills));
    const body = p.alive ? p.body : [];
    w.u16(body.length);
    for (const c of body) w.u16(c);
  }
  w.u16(engine.food.size);
  for (const f of engine.food.values()) {
    w.u16(f.cell);
    w.u8(f.kind);
  }
  w.u16(engine.obstacles.size);
  for (const c of engine.obstacles) w.u16(c);
  // Optional trailing section: lives per player, in player order.
  w.u8(engine.players.length);
  for (const p of engine.players) w.u8(wireLives(p.livesLeft));
  return w.done();
}

export function encodeDelta(engine: GameEngine, ev: TickEvents): Uint8Array {
  const w = new Writer(128);
  w.u8(BIN_DELTA);
  w.u32(ev.tick);
  w.u8(PHASES[engine.phase]);
  w.u8(engine.players.length);
  const moved = new Map(ev.moved.map((m) => [m.slot, m]));
  for (const p of engine.players) {
    const m = moved.get(p.slot);
    const fx = effectsOf(p, engine.tick);
    w.u8(p.slot);
    w.u8((p.alive ? FLAG_ALIVE : 0) | (m?.tailPopped ? 2 : 0) | (wireLives(p.livesLeft) << 2) | fireFlag(fx));
    w.u8(fx & 0xff);
    w.u8(p.dir);
    w.u16(wireScore(engine, p.score));
    w.u16(clamp16(p.kills));
    w.u16(m ? m.head : NO_CELL);
  }
  w.u16(ev.foodAdded.length);
  for (const f of ev.foodAdded) {
    w.u16(f.cell);
    w.u8(f.kind);
  }
  w.u16(ev.foodRemoved.length);
  for (const c of ev.foodRemoved) w.u16(c);
  w.u16(ev.obstaclesAdded.length);
  for (const c of ev.obstaclesAdded) w.u16(c);
  // Trailing sections, each written only if it or a later one is non-empty: full bodies of
  // snakes that came back (respawn arenas), speed-boost second moves, deaths (kill feed),
  // boost tail trims, electric shocks, and bonuses.
  const later5 = ev.bonuses.length > 0;
  const later4 = later5 || ev.shocked.length > 0;
  const later3 = later4 || ev.trimmed.length > 0;
  const later2 = later3 || ev.died.length > 0;
  const later1 = later2 || ev.extraMoves.length > 0;
  if (later1 || ev.respawned.length) {
    w.u8(ev.respawned.length);
    for (const r of ev.respawned) {
      w.u8(r.slot);
      w.u8(r.dir);
      w.u16(r.body.length);
      for (const c of r.body) w.u16(c);
    }
  }
  if (later1) {
    w.u8(ev.extraMoves.length);
    for (const m of ev.extraMoves) {
      w.u8(m.slot);
      w.u16(m.head);
      w.u8(m.tailPopped ? 1 : 0);
    }
  }
  if (later2) {
    w.u8(ev.died.length);
    for (const d of ev.died) {
      w.u8(d.slot);
      w.u8(d.killedBy ?? NO_KILLER);
      w.u8(Math.max(0, CAUSES.indexOf(d.cause)));
    }
  }
  if (later3) {
    w.u8(ev.trimmed.length);
    for (const slot of ev.trimmed) w.u8(slot);
  }
  if (later4) {
    w.u8(ev.shocked.length);
    for (const s of ev.shocked) {
      w.u8(s.slot);
      w.u16(s.length);
    }
  }
  if (later5) {
    w.u8(ev.bonuses.length);
    for (const b of ev.bonuses) {
      w.u8(b.slot);
      w.u8(BONUS_KINDS.indexOf(b.kind));
      w.u16(wireScore(engine, b.points));
    }
  }
  return w.done();
}

export function decodeServerBinary(data: Uint8Array): KeyframeMsg | DeltaMsg | { type: 'pong'; t: number } | null {
  const r = new Reader(data);
  const kind = r.u8();
  if (kind === BIN_KEYFRAME) {
    const tick = r.u32();
    const phase = r.u8();
    const width = r.u16();
    const height = r.u16();
    const n = r.u8();
    const players: WirePlayer[] = [];
    for (let i = 0; i < n; i++) {
      const slot = r.u8();
      const flags = r.u8();
      const effects = r.u8();
      const dir = r.u8() as Dir;
      const score = r.u16();
      const kills = r.u16();
      const len = r.u16();
      const body: number[] = new Array(len);
      for (let j = 0; j < len; j++) body[j] = r.u16();
      players.push({ slot, alive: (flags & FLAG_ALIVE) !== 0, team: (flags >> 1) & 0x7, effects: withFire(flags, effects), lives: 3, dir, score, kills, body });
    }
    const fc = r.u16();
    const food: WireFood[] = [];
    for (let i = 0; i < fc; i++) food.push({ cell: r.u16(), kind: r.u8() as FoodKind });
    const obstacles = r.u16list();
    if (r.remaining >= 1) {
      const ln = r.u8();
      for (let i = 0; i < ln && r.remaining >= 1; i++) {
        const lives = r.u8();
        if (players[i]) players[i]!.lives = lives;
      }
    }
    return { type: 'keyframe', tick, phase, width, height, players, food, obstacles };
  }
  if (kind === BIN_DELTA) {
    const tick = r.u32();
    const phase = r.u8();
    const n = r.u8();
    const players: DeltaMsg['players'] = [];
    for (let i = 0; i < n; i++) {
      const slot = r.u8();
      const flags = r.u8();
      const effects = r.u8();
      const dir = r.u8() as Dir;
      const score = r.u16();
      const kills = r.u16();
      const head = r.u16();
      players.push({
        slot,
        alive: (flags & FLAG_ALIVE) !== 0,
        tailPopped: (flags & 2) !== 0,
        effects: withFire(flags, effects),
        lives: (flags >> 2) & 0x3,
        dir,
        score,
        kills,
        head: head === NO_CELL ? null : head,
      });
    }
    const fa = r.u16();
    const foodAdded: WireFood[] = [];
    for (let i = 0; i < fa; i++) foodAdded.push({ cell: r.u16(), kind: r.u8() as FoodKind });
    const fr = r.u16();
    const foodRemoved: number[] = [];
    for (let i = 0; i < fr; i++) foodRemoved.push(r.u16());
    const obstaclesAdded = r.u16list();
    const respawned: DeltaMsg['respawned'] = [];
    const rn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < rn; i++) {
      const slot = r.u8();
      const dir = r.u8() as Dir;
      const len = r.u16();
      const body: number[] = new Array(len);
      for (let j = 0; j < len; j++) body[j] = r.u16();
      respawned.push({ slot, dir, body });
    }
    const extraMoves: DeltaMsg['extraMoves'] = [];
    const xn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < xn; i++) extraMoves.push({ slot: r.u8(), head: r.u16(), tailPopped: r.u8() === 1 });
    const deaths: DeltaMsg['deaths'] = [];
    const dn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < dn; i++) {
      const slot = r.u8();
      const killer = r.u8();
      deaths.push({ slot, killedBy: killer === NO_KILLER ? null : killer, cause: CAUSES[r.u8()] ?? 'wall' });
    }
    const trimmed: number[] = [];
    const tn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < tn; i++) trimmed.push(r.u8());
    const shocked: DeltaMsg['shocked'] = [];
    const sn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < sn; i++) shocked.push({ slot: r.u8(), length: r.u16() });
    const bonuses: DeltaMsg['bonuses'] = [];
    const bn = r.remaining >= 1 ? r.u8() : 0;
    for (let i = 0; i < bn; i++) {
      const slot = r.u8();
      const kind = BONUS_KINDS[r.u8()];
      const points = r.u16();
      if (kind) bonuses.push({ slot, kind, points });
    }
    return { type: 'delta', tick, phase, players, foodAdded, foodRemoved, obstaclesAdded, respawned, extraMoves, deaths, trimmed, shocked, bonuses };
  }
  if (kind === BIN_PONG) return { type: 'pong', t: r.u32() };
  return null;
}

/** Client -> server: [BIN_INPUT, seq u16, dir u8]. 4 bytes per turn. */
export function encodeInput(seq: number, dir: Dir): Uint8Array {
  return new Uint8Array([BIN_INPUT, (seq >> 8) & 0xff, seq & 0xff, dir]);
}
/**
 * Client -> server: [BIN_INPUT_AT, tick u32, dir u8]. A turn the client has already shown on its
 * own snake as part of `tick`: the server holds it until the step that makes that tick, so both
 * agree on which cell the snake turned at. If it arrives too late for that, it's taken at the
 * next step, like a plain BIN_INPUT.
 */
export function encodeInputAt(tick: number, dir: Dir): Uint8Array {
  const w = new Writer(6);
  w.u8(BIN_INPUT_AT);
  w.u32(tick >>> 0);
  w.u8(dir);
  return w.done();
}
export function encodePing(t: number): Uint8Array {
  const w = new Writer(5);
  w.u8(BIN_PING);
  w.u32(t >>> 0);
  return w.done();
}
export function encodePong(t: number): Uint8Array {
  const w = new Writer(5);
  w.u8(BIN_PONG);
  w.u32(t >>> 0);
  return w.done();
}
export function decodeClientBinary(
  data: Uint8Array,
): { type: 'input'; seq: number; dir: Dir } | { type: 'inputAt'; tick: number; dir: Dir } | { type: 'ping'; t: number } | null {
  if (data.length === 4 && data[0] === BIN_INPUT) {
    const dir = data[3]!;
    if (dir > 3) return null;
    return { type: 'input', seq: (data[1]! << 8) | data[2]!, dir: dir as Dir };
  }
  if (data.length === 6 && data[0] === BIN_INPUT_AT) {
    const dir = data[5]!;
    if (dir > 3) return null;
    return { type: 'inputAt', tick: new Reader(data.subarray(1)).u32(), dir: dir as Dir };
  }
  if (data.length === 5 && data[0] === BIN_PING) {
    return { type: 'ping', t: new Reader(data.subarray(1)).u32() };
  }
  return null;
}
